/**
 * Property editor panel for particle effects.
 * Renders grouped sliders, color pickers, and number inputs based on the selected effect type.
 * Supports mode-aware filtering: sliders/groups tagged with mode: 'normal' | 'afterburner'
 * are only shown when that mode is active. Untagged items are always shown.
 */

import { useCallback, useRef } from 'react';
import { ForgeColorPicker } from '../hud/settings/ForgeColorPicker.js';
import type {
  ParticleEffectId,
  ThrusterFlameConfig,
  ThrusterSparksConfig,
  AtmosphereWispsConfig,
  RGBColor,
} from '@warmancer/shared';
import styles from './ParticleEditor.module.css';

type EffectConfig = ThrusterFlameConfig | ThrusterSparksConfig | AtmosphereWispsConfig;

export type ThrusterMode = 'normal' | 'afterburner';

interface PropertyPanelProps {
  readonly effectId: ParticleEffectId;
  readonly config: EffectConfig;
  readonly onChange: (partial: Partial<EffectConfig>) => void;
  readonly mode?: ThrusterMode;
}

interface SliderDef {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  decimals?: number;
  mode?: ThrusterMode;
}

interface ColorDef {
  key: string;
  label: string;
  max?: number;
  mode?: ThrusterMode;
}

interface GroupDef {
  label: string;
  mode?: ThrusterMode;
  sliders?: SliderDef[];
  colors?: ColorDef[];
  intInputs?: { key: string; label: string; min: number; max: number; mode?: ThrusterMode }[];
}

// ---- Flame property groups ----

const FLAME_GROUPS: GroupDef[] = [
  {
    label: 'Geometry',
    sliders: [
      { key: 'flameWidth', label: 'Width', min: 0.1, max: 3.0, step: 0.05 },
      { key: 'flameLength', label: 'Length', min: 1.0, max: 10.0, step: 0.1 },
    ],
  },
  {
    label: 'Taper',
    sliders: [
      { key: 'nozzleWidth', label: 'Nozzle Width', min: 0.01, max: 0.5, step: 0.01 },
      { key: 'peakWidth', label: 'Peak Width', min: 0.1, max: 1.0, step: 0.01 },
    ],
  },
  {
    label: 'Flame Length',
    sliders: [
      { key: 'flameLenNormal', label: 'Length', min: 0.2, max: 1.0, step: 0.01, mode: 'normal' },
      { key: 'flameLenAfterburner', label: 'Length', min: 0.2, max: 1.0, step: 0.01, mode: 'afterburner' },
    ],
  },
  {
    label: 'Turbulent Edges',
    sliders: [
      { key: 'flowSpeed', label: 'Flow Speed', min: 1.0, max: 20.0, step: 0.5 },
      { key: 'flowSpeedBoost', label: 'AB Boost', min: 0.0, max: 10.0, step: 0.5 },
      { key: 'distortionBase', label: 'Distortion Base', min: 0.0, max: 0.5, step: 0.01 },
      { key: 'distortionTipAdd', label: 'Tip Add', min: 0.0, max: 0.5, step: 0.01 },
    ],
  },
  {
    label: 'Mach Diamonds',
    sliders: [
      { key: 'machFreqNormal', label: 'Frequency', min: 2.0, max: 30.0, step: 0.5, mode: 'normal' },
      { key: 'machFreqAfterburner', label: 'Frequency', min: 2.0, max: 30.0, step: 0.5, mode: 'afterburner' },
      { key: 'machBandMin', label: 'Band Min', min: 0.0, max: 1.0, step: 0.01 },
      { key: 'machBandRange', label: 'Band Range', min: 0.0, max: 1.0, step: 0.01 },
    ],
  },
  {
    label: 'Internal Turbulence',
    sliders: [
      { key: 'turbCoarseScale', label: 'Coarse Scale', min: 1.0, max: 10.0, step: 0.5 },
      { key: 'turbFineScale', label: 'Fine Scale', min: 1.0, max: 20.0, step: 0.5 },
      { key: 'turbBase', label: 'Base', min: 0.0, max: 1.0, step: 0.01 },
      { key: 'turbCoarseAmp', label: 'Coarse Amp', min: 0.0, max: 0.5, step: 0.01 },
      { key: 'turbFineAmp', label: 'Fine Amp', min: 0.0, max: 0.5, step: 0.01 },
    ],
  },
  {
    label: 'Flicker',
    sliders: [
      { key: 'flickerGlobalFreq', label: 'Global Freq', min: 10.0, max: 100.0, step: 1.0 },
      { key: 'flickerGlobalAmp', label: 'Global Amp', min: 0.0, max: 0.5, step: 0.01 },
      { key: 'flickerLocalFreq', label: 'Local Freq', min: 10.0, max: 100.0, step: 1.0 },
      { key: 'flickerLocalAmp', label: 'Local Amp', min: 0.0, max: 0.5, step: 0.01 },
    ],
  },
  {
    label: 'Colors',
    mode: 'normal',
    colors: [
      { key: 'normalCore', label: 'Core' },
      { key: 'normalMid', label: 'Mid' },
      { key: 'normalTip', label: 'Tip' },
    ],
  },
  {
    label: 'Colors',
    mode: 'afterburner',
    colors: [
      { key: 'abCore', label: 'Core' },
      { key: 'abMid', label: 'Mid' },
      { key: 'abTip', label: 'Tip' },
    ],
  },
  {
    label: 'HDR',
    sliders: [
      { key: 'hdrMultNormal', label: 'Multiplier', min: 0.5, max: 10.0, step: 0.1, mode: 'normal' },
      { key: 'hdrMultAfterburner', label: 'Multiplier', min: 0.5, max: 10.0, step: 0.1, mode: 'afterburner' },
      { key: 'nozzleBoostMax', label: 'Nozzle Boost', min: 0.0, max: 5.0, step: 0.1 },
    ],
  },
];

// ---- Spark property groups ----

const SPARK_GROUPS: GroupDef[] = [
  {
    label: 'Particles',
    intInputs: [
      { key: 'sparkCount', label: 'Count', min: 4, max: 256 },
    ],
    sliders: [
      { key: 'plumeSeedThreshold', label: 'Plume Threshold', min: 0.1, max: 0.9, step: 0.05 },
    ],
  },
  {
    label: 'Lifetimes',
    sliders: [
      { key: 'sparkLifetime', label: 'Spark', min: 0.1, max: 2.0, step: 0.05 },
      { key: 'plumeLifetime', label: 'Plume', min: 0.1, max: 3.0, step: 0.05 },
    ],
  },
  {
    label: 'Speed',
    sliders: [
      { key: 'sparkSpeed', label: 'Spark', min: 1.0, max: 20.0, step: 0.5 },
      { key: 'plumeSpeed', label: 'Plume', min: 1.0, max: 15.0, step: 0.5 },
      { key: 'afterburnerSpeedBoost', label: 'AB Boost', min: 0.0, max: 2.0, step: 0.1 },
    ],
  },
  {
    label: 'Spread',
    sliders: [
      { key: 'coneAngle', label: 'Cone Angle', min: 0.1, max: 2.0, step: 0.05 },
      { key: 'nozzleHalfWidth', label: 'Nozzle Width', min: 0.01, max: 0.5, step: 0.01 },
    ],
  },
  {
    label: 'Drag',
    sliders: [
      { key: 'sparkDrag', label: 'Spark', min: 0.5, max: 10.0, step: 0.5 },
      { key: 'plumeDrag', label: 'Plume', min: 0.5, max: 10.0, step: 0.5 },
    ],
  },
  {
    label: 'Size',
    sliders: [
      { key: 'sparkSize', label: 'Spark (px)', min: 1.0, max: 12.0, step: 0.5 },
      { key: 'plumeSize', label: 'Plume (px)', min: 1.0, max: 20.0, step: 0.5 },
      { key: 'sparkEdgeSoftness', label: 'Spark Edge', min: 0.0, max: 1.0, step: 0.05 },
      { key: 'plumeEdgeSoftness', label: 'Plume Edge', min: 0.0, max: 1.0, step: 0.05 },
    ],
  },
  {
    label: 'Colors',
    mode: 'normal',
    colors: [
      { key: 'normalHot', label: 'Hot' },
      { key: 'normalMid', label: 'Mid' },
      { key: 'normalCool', label: 'Cool' },
    ],
  },
  {
    label: 'Colors',
    mode: 'afterburner',
    colors: [
      { key: 'abHot', label: 'Hot' },
      { key: 'abMid', label: 'Mid' },
      { key: 'abCool', label: 'Cool' },
    ],
  },
  {
    label: 'Flicker',
    sliders: [
      { key: 'flickerFreq', label: 'Frequency', min: 10.0, max: 100.0, step: 1.0 },
      { key: 'flickerAmp', label: 'Amplitude', min: 0.0, max: 0.5, step: 0.01 },
    ],
  },
  {
    label: 'HDR',
    sliders: [
      { key: 'hdrMultNormal', label: 'Multiplier', min: 0.5, max: 10.0, step: 0.1, mode: 'normal' },
      { key: 'hdrMultAfterburner', label: 'Multiplier', min: 0.5, max: 10.0, step: 0.1, mode: 'afterburner' },
    ],
  },
];

// ---- Atmosphere property groups ----

const ATMOSPHERE_GROUPS: GroupDef[] = [
  {
    label: 'Emitter',
    sliders: [
      { key: 'radiusMultiplier', label: 'Radius Mult', min: 1.0, max: 2.0, step: 0.05 },
      { key: 'thickness', label: 'Thickness', min: 0.01, max: 0.5, step: 0.01 },
      { key: 'emissionRate', label: 'Emission Rate', min: 1, max: 100, step: 1 },
    ],
  },
  {
    label: 'Particle',
    sliders: [
      { key: 'lifetimeMin', label: 'Life Min', min: 0.5, max: 10.0, step: 0.5 },
      { key: 'lifetimeMax', label: 'Life Max', min: 0.5, max: 20.0, step: 0.5 },
      { key: 'startSpeed', label: 'Start Speed', min: 0.01, max: 2.0, step: 0.01 },
      { key: 'sizeMin', label: 'Size Min', min: 0.1, max: 5.0, step: 0.1 },
      { key: 'sizeMax', label: 'Size Max', min: 0.1, max: 10.0, step: 0.1 },
    ],
  },
  {
    label: 'Behaviors',
    sliders: [
      { key: 'orbitSpeed', label: 'Orbit Speed', min: 0.0, max: 2.0, step: 0.05 },
    ],
  },
];

const GROUP_MAP: Record<ParticleEffectId, GroupDef[]> = {
  thruster_flame: FLAME_GROUPS,
  thruster_sparks: SPARK_GROUPS,
  atmosphere_wisps: ATMOSPHERE_GROUPS,
};

/** Filter groups and their contents based on the active mode. */
function filterGroupsForMode(groups: GroupDef[], mode: ThrusterMode | undefined): GroupDef[] {
  if (mode === undefined) return groups;

  const result: GroupDef[] = [];
  for (const group of groups) {
    // Skip entire group if it has a mode tag that doesn't match
    if (group.mode !== undefined && group.mode !== mode) continue;

    // Filter individual items within the group
    const filteredSliders = group.sliders?.filter((s) => s.mode === undefined || s.mode === mode);
    const filteredColors = group.colors?.filter((c) => c.mode === undefined || c.mode === mode);
    const filteredIntInputs = group.intInputs?.filter((i) => i.mode === undefined || i.mode === mode);

    // Skip the group if ALL items were filtered out
    const hasContent =
      (filteredSliders && filteredSliders.length > 0) ||
      (filteredColors && filteredColors.length > 0) ||
      (filteredIntInputs && filteredIntInputs.length > 0);

    if (!hasContent) continue;

    result.push({
      ...group,
      sliders: filteredSliders,
      colors: filteredColors,
      intInputs: filteredIntInputs,
    });
  }
  return result;
}

const REPEAT_DELAY = 400;
const REPEAT_INTERVAL = 100;

/** Custom integer input with visible +/- buttons and hold-to-repeat. */
function NumberStepper({
  value,
  min,
  max,
  onChange: onValueChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: string) => void;
}) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const clearTimers = useCallback(() => {
    if (timeoutRef.current !== null) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (intervalRef.current !== null) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  const startRepeat = useCallback((delta: number) => {
    const step = () => {
      const next = Math.max(min, Math.min(max, valueRef.current + delta));
      valueRef.current = next;
      onValueChange(String(next));
    };
    step();
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(step, REPEAT_INTERVAL);
    }, REPEAT_DELAY);
  }, [min, max, onValueChange]);

  return (
    <div className={styles.numberStepper}>
      <button
        type="button"
        className={styles.numberStepperButtonLeft}
        onPointerDown={() => startRepeat(-1)}
        onPointerUp={clearTimers}
        onPointerLeave={clearTimers}
      >
        &minus;
      </button>
      <input
        type="number"
        className={styles.numberInput}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onValueChange(e.target.value)}
      />
      <button
        type="button"
        className={styles.numberStepperButtonRight}
        onPointerDown={() => startRepeat(1)}
        onPointerUp={clearTimers}
        onPointerLeave={clearTimers}
      >
        +
      </button>
    </div>
  );
}

export function ParticleEditorPropertyPanel({ effectId, config, onChange, mode }: PropertyPanelProps) {
  const allGroups = GROUP_MAP[effectId] ?? [];
  const groups = filterGroupsForMode(allGroups, mode);

  const handleSliderChange = useCallback((key: string, value: number) => {
    onChange({ [key]: value } as Partial<EffectConfig>);
  }, [onChange]);

  const handleIntChange = useCallback((key: string, value: string) => {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      onChange({ [key]: parsed } as Partial<EffectConfig>);
    }
  }, [onChange]);

  const handleColorChange = useCallback((key: string, color: RGBColor) => {
    onChange({ [key]: color } as Partial<EffectConfig>);
  }, [onChange]);

  return (
    <div className={styles.rightPanel}>
      {groups.map((group, gi) => (
        <div key={`${group.label}-${gi}`} className={styles.propertyGroup}>
          <div className={styles.propertyGroupLabel}>{group.label}</div>

          {group.intInputs?.map((input) => (
            <div key={input.key} className={styles.propertyRow}>
              <span className={styles.propertyLabel}>{input.label}</span>
              <NumberStepper
                value={(config as unknown as Record<string, number>)[input.key] ?? 0}
                min={input.min}
                max={input.max}
                onChange={(v) => handleIntChange(input.key, v)}
              />
            </div>
          ))}

          {group.sliders?.map((slider) => (
            <div key={slider.key} className={styles.propertyRow}>
              <span className={styles.propertyLabel}>{slider.label}</span>
              <input
                type="range"
                className={styles.propertySlider}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                value={(config as unknown as Record<string, number>)[slider.key] ?? 0}
                onChange={(e) => handleSliderChange(slider.key, Number(e.target.value))}
              />
              <span className={styles.propertyValue}>
                {((config as unknown as Record<string, number>)[slider.key] ?? 0).toFixed(slider.decimals ?? 2)}
              </span>
            </div>
          ))}

          {group.colors?.map((color) => (
            <ForgeColorPicker
              key={color.key}
              label={color.label}
              value={(config as unknown as Record<string, RGBColor>)[color.key] ?? [0, 0, 0]}
              onChange={(c) => handleColorChange(color.key, c)}
              max={color.max}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
